// src/components/LayerPanel/ImageryTab.jsx
//
// IMAGERY scope: an ORIENTATION surface, not a layer picker.
//
// ── WHY THE LAYER LIST IS GONE ──────────────────────────────────────────────
//
// The time strip chooses what renders, so the per-series list of acquisition
// pills was a second way to do one thing. What the strip CANNOT do is
// orientation, and that is structural rather than a gap: the strip is
// viewport-scoped by design -- it shows what covers where you are looking. Pan
// into a gap and it empties, and nothing distinguishes "you left coverage" from
// "something broke". Wanting to FIND coverage means panning around hunting for
// it.
//
// So this tab answers "where is imagery" instead. One row per entitled collect:
// when it was flown, how much ground it actually covers, how many separate
// pieces that is, and the county summary.
//
// Accounting -- what an org owns, what they bought -- is deliberately NOT here.
// That belongs on the platform-admin entitlement surface; a map's layer panel
// answering it is how the two got conflated in the first place.
//
// ── NO REQUEST ──────────────────────────────────────────────────────────────
//
// Everything comes from the catalog already in the store.
// groupFootprintsIntoSeries carries `geometry` per acquisition (the dissolved
// fine footprint, kept deliberately) and `counties` at both levels. Entitlement
// scoping is therefore already correct and unchanged: the catalog comes from
// /imagery/footprints, bound to the caller by
// `join user_orgs uo on uo.org_id = e.org_id where uo.user_id = $1`. Showing a
// collect a customer cannot see would need this tab to ask a DIFFERENT question
// than the strip, and it does not ask any.
//
// ── WHAT IS NOT HERE YET ────────────────────────────────────────────────────
//
// Fly-to and the out-of-coverage case are later commits. Fly-to needs the parts
// measured rather than guessed -- see partCount's note on why "the first part"
// is a trap -- and out-of-coverage needs its cost measured before it is built.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, X, LocateFixed } from "lucide-react";

import { useImageryStackStore } from "../../stores/useImageryStackStore";
import { useCoverageFlyoutStore } from "../../stores/useCoverageFlyoutStore";
import { useMapActions } from "../../stores/useMapActions";
import { collectRows } from "../../layers/imagery/collectSummary";
import SeriesCoverageLine from "./SeriesCoverageLine";
import CountyDetailFlyout from "./CountyDetailFlyout";

function cx(...c) { return c.filter(Boolean).join(" "); }

/** "2025_12_13" -> "13 Dec 2025". The stored form is a key, not a label. */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatDate(d) {
  const m = /^(\d{4})_(\d{2})_(\d{2})$/.exec(String(d ?? ""));
  if (!m) return String(d ?? "");
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${Number(m[3])} ${mon} ${m[1]}` : String(d);
}

/** Whole km2 with thousands separators; areas here run 721 to 9,886. */
function formatArea(km2) {
  if (km2 == null || !Number.isFinite(km2)) return null;
  return `${Math.round(km2).toLocaleString()} km²`;
}

export default function ImageryTab() {
  const catalog = useImageryStackStore((s) => s.catalog);

  // Registered by MapView once the map loads (setZoomHandler, MapView.jsx:1598)
  // and NULL until then -- the store's own default. So this is typechecked at
  // click time rather than called through `?.`: null here is a real, expected
  // state, and the guard says which one it is instead of silently doing nothing.
  const zoomToBounds = useMapActions((s) => s.zoomToBounds);
  const [search, setSearch] = useState("");
  const [flyoutKey, setFlyoutKey] = useState(null);

  const rows = useMemo(() => collectRows(catalog?.series), [catalog?.series]);

  // Restores the ONE affordance the collect list dropped when SeriesRow was
  // retired: SeriesRow could zoom to an acquisition WITHOUT adding it to the
  // map, and ActiveLayers' "Zoom to layer" only reaches layers already in the
  // stack. Browse-then-zoom comes back here, and this row still adds nothing.
  //
  // bounds is [[w,s],[e,n]] on the catalog and the handler wants a flat
  // [w,s,e,n] -- it checks length === 4 and drops anything else on the floor,
  // so passing the nested pair would fail silently rather than throw.
  const flyTo = useCallback((bounds) => {
    if (typeof zoomToBounds !== "function" || !bounds) return;
    zoomToBounds([bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]]);
  }, [zoomToBounds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    // Matches the collect's own label AND its series name, because a user
    // searching "midland" and a user searching "december" are both looking for
    // the same row.
    return rows.filter((r) =>
      `${r.seriesName} ${formatDate(r.date)} ${r.date}`.toLowerCase().includes(q));
  }, [rows, search]);

  const flyoutRow = flyoutKey ? rows.find((r) => r.key === flyoutKey) : null;

  // ── THE CROSS-BOUNDARY CONTRACT, PRESERVED DELIBERATELY ───────────────────
  //
  // MapView reads useCoverageFlyoutStore (:288) to render-gate two blocks
  // (:2054, :2126) -- the change-detection and compare controls hide while the
  // flyout is open so they do not sit under it. This tab is the only publisher.
  //
  // The flyout's DOORWAY used to be the chevron on a SeriesRow, and SeriesRow is
  // what this commit retires -- so without moving the publisher, retiring the
  // list would silently strand both the county panel and MapView's gating. The
  // chevron now lives on each collect row instead, and this effect is unchanged.
  const setCoverageFlyoutOpen = useCoverageFlyoutStore((s) => s.setOpen);
  useEffect(() => {
    setCoverageFlyoutOpen(!!flyoutRow);
    return () => setCoverageFlyoutOpen(false);
  }, [flyoutRow, setCoverageFlyoutOpen]);

  return (
    <div className="flex flex-col">
      <div className="px-2 py-2 space-y-2">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between px-0.5">
            <span className="text-[10px] font-bold font-mono uppercase tracking-[0.14em] text-gray-400">
              Collects
            </span>
            <span className="text-[9px] font-mono text-fg-secondary">
              {filtered.length}
              {filtered.length !== rows.length ? ` / ${rows.length}` : ""}
            </span>
          </div>

          <div className="relative">
            <Search
              size={11}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-secondary pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search collects"
              aria-label="Search collects"
              className={cx(
                "w-full rounded bg-white/[0.04] border border-bg-border",
                "pl-7 pr-7 py-1 text-[11px] text-fg-primary placeholder:text-fg-secondary",
                "outline-none focus:border-cyan-400/40",
              )}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-gray-200"
              >
                <X size={11} />
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="px-1 py-3 text-[10px] text-fg-secondary">
              {rows.length === 0
                ? "No imagery is available to your organization."
                : "No collect matches that search."}
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((r) => (
                <div
                  key={r.key}
                  data-testid={`collect-row-${r.date}`}
                  className="rounded border border-bg-border bg-white/[0.02]"
                >
                  <div className="px-2 pt-1.5 flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-fg-primary">{formatDate(r.date)}</div>
                      <div className="text-[10px] text-fg-secondary truncate">{r.seriesName}</div>
                      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 text-[10px] text-fg-secondary">
                        {formatArea(r.areaKm2) && (
                          <span className="tabular-nums">{formatArea(r.areaKm2)}</span>
                        )}
                        {/* PARTS ARE SHOWN BECAUSE THE COVERAGE IS DISJOINT AND
                            THAT IS NOT VISIBLE OTHERWISE. Measured 2026-09-07:
                            2025_12_13 is 19 separate pieces whose largest holds
                            22.1% of the total, so a single "9,886 km²" reads as
                            one block of ground and is not. One piece says so by
                            saying nothing. */}
                        {r.parts > 1 && (
                          <span className="tabular-nums">{r.parts} areas</span>
                        )}
                      </div>
                      </div>

                    {/* Same icon and the same call as ActiveLayers' "Zoom to
                        layer" -- the difference is only that this one reaches a
                        collect that is not on the map. Absent when the collect
                        has no bounds, which is honest: there is nowhere to go. */}
                    {r.bounds && (
                      <button
                        type="button"
                        title="Zoom to collect"
                        aria-label={`Zoom to ${formatDate(r.date)}`}
                        onClick={() => flyTo(r.bounds)}
                        className="flex-shrink-0 p-1 rounded text-fg-secondary hover:text-accent-action hover:bg-white/5"
                      >
                        <LocateFixed size={11} />
                      </button>
                    )}
                  </div>

                  {/* The county summary and the chevron: unchanged components,
                      re-mounted here rather than on a retired SeriesRow. */}
                  <SeriesCoverageLine
                    series={r.series}
                    onOpenFlyout={() => setFlyoutKey(r.key)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {flyoutRow && (
        <CountyDetailFlyout
          series={flyoutRow.series}
          // The collect this row IS, rather than "whichever acquisitions of this
          // series are currently pinned" -- pinning is what the list did and is
          // gone. One row, one collect, one selection.
          selectedAcquisitions={[flyoutRow.acq]}
          aoiCounties={catalog?.aoi_counties ?? null}
          onClose={() => setFlyoutKey(null)}
        />
      )}
    </div>
  );
}
